import { useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { toast } from "react-toastify";
import { Button, ButtonVariant, Show } from "shared/components";
import { VerificationStatus } from "shared/types";
import { CombinedIncome } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";
import {
  ReverifyRequest,
  ReverifyRequestStatus,
  ReverifyStatus
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/reverify";
import {
  getVerificationProcessingStatus,
  getReverifyDetails,
  getStatusConfig
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/utils";
import { ProcessingIcon } from "./ProcessingIcon";
import { getErrorMessage } from "utils/error";
import { useAuthContext } from "providers/Auth";
import { FEATURES } from "types/landlord/partner";

interface Props {
  income: CombinedIncome;
  applicationId?: number | string;
  verificationId?: number | string;
  applicantId?: number | string;
  reverifyRequests?: ReverifyRequest[];
  verificationStatus?: VerificationStatus;
  onCancelled?: () => void;
}

export const ProcessingStatus = ({
  income,
  applicationId,
  verificationId,
  applicantId,
  reverifyRequests = [],
  verificationStatus,
  onCancelled
}: Props) => {
  const { partner } = useAuthContext();
  const isSelfServeReverifyEnabled =
    partner?.features.includes(FEATURES.WIP_FEAT_INCOME_SELF_SERVE_REVERIFY) ??
    false;
  const pendingRequest = reverifyRequests.find(
    (request) =>
      request.income_id === income.id &&
      request.status === ReverifyRequestStatus.Pending
  );

  const { mutate, isPending } = useMutation({
    mutationKey: [
      "CANCEL_REVERIFY_REQUEST",
      "APPLICATION",
      String(applicationId),
      "VERIFICATION",
      String(verificationId),
      "REQUEST",
      String(pendingRequest?.id)
    ],
    mutationFn: (pendingReverifyRequest: ReverifyRequest) =>
      instance.post(
        `portal/v1/applications/${applicationId}/verifications/${verificationId}/reverify_requests/cancel?applicant_id=${applicantId}`,
        { request_id: pendingReverifyRequest.id }
      ),
    onError: (err) => {
      toast.error(
        getErrorMessage(err, "Failed to cancel re-verification request")
      );
    },
    onSuccess: () => {
      toast.success("Re-verification request canceled");
      onCancelled?.();
    }
  });

  if (income.status !== "processing") return null;

  const status = isSelfServeReverifyEnabled
    ? getVerificationProcessingStatus(
        income,
        reverifyRequests,
        verificationStatus
      )
    : ReverifyStatus.InitialVerification;

  const details =
    status !== ReverifyStatus.InitialVerification
      ? getReverifyDetails(income, reverifyRequests, verificationStatus)
      : null;

  const { title, description, showDate, showCancelButton } = getStatusConfig(
    status,
    details
  );

  return (
    <div className="flex flex-col items-center justify-center min-h-[250px] w-full mx-auto">
      <ProcessingIcon />

      <h2 className="text-base font-bold text-content-500 mb-2 text-center">
        {title}
      </h2>

      <p className="text-center text-sm font-medium text-content-300 mb-4">
        {description}
        <br />
        <Show if={showDate && details?.formattedDate}>
          on {details?.formattedDate}
        </Show>
      </p>

      <Show if={showCancelButton && Boolean(pendingRequest)}>
        <Button
          variant={ButtonVariant.BASE}
          loading={isPending}
          onClick={() => {
            if (pendingRequest) mutate(pendingRequest);
          }}
        >
          Cancel request
        </Button>
      </Show>
    </div>
  );
};
